// The canonical shape of the profile editor's person fields and the single
// mapping from an edited field to its genesis UpdatePerson patch.
//
// Extracted from ProfilePage so BOTH the manual-edit SaveBar and the in-line
// merge commit (LinkedIn + CV import) write person fields the SAME way — a
// reference field becomes `{ id }`, citizenship an id-list, child count an int,
// everything else a nullable scalar. Sharing it is what lets an imported field
// (gender, marital status, citizenship, …) commit with full parity instead of a
// second, drift-prone mapping.

// Empty profile shape, used while data is loading (no mock). Its keys define
// ProfileKey — the set of editable person fields.
export const EMPTY_PROFILE = {
  name: "",
  givenName: "",
  familyName: "",
  role: "",
  phone: "",
  email: "",
  city: "",
  country: "",
  gender: "",
  birthday: "",
  citizenship: "",
  summary: "",
  website: "",
  nationality: "",
  age: "",
  maritalStatus: "",
  childCount: "",
  street: "",
  houseNumber: "",
  apartment: "",
  zipCode: "",
  profession: "",
  currentActivity: "",
  currentEmployer: "",
  jobDepartment: "",
  jobLocation: "",
  drivingLicence: "",
  miscellaneous: "",
  currentTasks: "",
  countryState: "",
  contractType: "",
  workingTimeModel: "",
};

export type ProfileKey = keyof typeof EMPTY_PROFILE;

// A partial UpdatePersonDataInput. Deliberately loose (Record) so the mapping
// can emit relation objects (`{ id }`), id-lists, ints, and null without the
// caller threading the generated input type through every branch.
export type DataPatch = Record<string, unknown>;

// One edited person field → its genesis UpdatePerson patch. `val` is the machine
// value the editor holds (a reference field's id, citizenship as comma-joined
// ids, childCount as a numeric string, dates as `YYYY`/`YYYY-MM`/`YYYY-MM-DD`).
// `name` and `age` are handled outside this mapping (name is split from
// given/family by the caller; age is derived + read-only), so they map to {}.
export function dataForField(key: ProfileKey, val: string): DataPatch {
  const nullable = val === "" ? null : val;
  switch (key) {
    case "gender":
      return { gender: val ? { id: val } : null };
    case "country":
      return { country: val ? { id: val } : null };
    case "maritalStatus":
      return { maritalStatus: val ? { id: val } : null };
    case "countryState":
      return { countryState: val ? { id: val } : null };
    case "jobDepartment":
      return { jobDepartment: val ? { id: val } : null };
    case "jobLocation":
      return { jobLocation: val ? { id: val } : null };
    case "contractType":
      return { contractType: val ? { id: val } : null };
    case "workingTimeModel":
      return { workingTimeModel: val ? { id: val } : null };
    case "citizenship": {
      const ids = val ? val.split(",").filter(Boolean) : [];
      return { citizenships: ids.map((id) => ({ id })) };
    }
    case "childCount": {
      const n = val === "" ? null : Number.parseInt(val, 10);
      return { childCount: Number.isFinite(n) ? n : null };
    }
    case "birthday":
      return { birthDate: nullable };
    case "summary":
      return { bio: nullable };
    case "name":
      // Name editing is deferred (genesis stores given/family separately).
      return {};
    case "role":
      return { jobTitle: nullable };
    case "age":
      // Read-only display, derived from birthDate.
      return {};
    default:
      return { [key]: nullable };
  }
}
